import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, type SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { floatToCurrencyValue } from 'app/shared-components/util/Currency';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnFilter, FilterOption } from 'util/filtering/models';
import { CriteriaRuleSelect } from './CriteriaRuleSelect';
import { CurrencyInputBox } from './CurrencyInputBox';
import { TextCriteriaContent } from './TextCriteriaContent';
import { SearchableTextCriteriaContent } from './SearchableTextCriteriaContent';

export type CriteriaRuleOption = { value: string; label: string };

export interface FilterChangeProps {
  selectedOptions?: FilterOption[];
  minNumber?: number;
  maxNumber?: number;
  key: string;
  active?: boolean;
}

interface CriteriaColumnItemProps {
  filter: ColumnFilter;
  isLast: boolean;
  onRuleChange: (event: SelectChangeEvent, key: string) => void;
  onFilterChange: (props: FilterChangeProps) => void;
}

// SEE-1897: a saved $0 bound shows as $0, not as an empty box.
const dollars = (cents?: number) => (cents == null ? '' : cents / 100);

export const CriteriaColumnItem = ({
  filter,
  isLast,
  onRuleChange,
  onFilterChange,
}: CriteriaColumnItemProps) => {
  const handleCriteriaRuleChange = (event: SelectChangeEvent) => {
    onRuleChange(event, filter.key);
  };

  const { t } = useTranslation();
  const theme = useTheme();

  const handleMinNumberChange = (value: string) => {
    const numericValue = value ? floatToCurrencyValue(Number(value.replace('$', ''))) : undefined;
    onFilterChange({
      key: filter.key,
      minNumber: numericValue,
      maxNumber: filter.maxNumber,
      active: numericValue !== undefined || filter.maxNumber !== undefined,
    });
  };

  const handleMaxNumberChange = (value: string) => {
    const numericValue = value ? floatToCurrencyValue(Number(value.replace('$', ''))) : undefined;
    onFilterChange({
      key: filter.key,
      minNumber: filter.minNumber,
      maxNumber: numericValue,
      active: filter.minNumber !== undefined || numericValue !== undefined,
    });
  };

  const handleSelectedOptionsChange = (newOptions: FilterOption[], key: string) => {
    let finalOptions = newOptions;

    if (key === 'status') {
      const hasRemoved = newOptions.find((opt) => opt.id === 'Removed');
      const hasActiveStatuses = newOptions.find(
        (opt) => opt.id === 'Registered' || opt.id === 'Not signed up yet'
      );
      const hadRemoved = filter.selectedValues?.find((opt) => opt.id === 'Removed');

      if (hasRemoved && hasActiveStatuses) {
        const removedJustAdded = hasRemoved && !hadRemoved;

        if (removedJustAdded) {
          finalOptions = [hasRemoved];
        } else {
          finalOptions = newOptions.filter(
            (opt) => opt.id === 'Registered' || opt.id === 'Not signed up yet'
          );
        }
      }
    }

    onFilterChange({
      key,
      selectedOptions: finalOptions,
      active: true,
    });
  };

  const handleClearFilter = (key: string) => {
    onFilterChange({
      key,
      selectedOptions: [],
      active: false,
    });
  };

  const criteriaRuleOptions = useMemo(() => {
    switch (filter.type) {
      case 'string':
        return [
          {
            value: 'allExcept',
            label: `All ${filter.name} except`,
          },
          {
            value: 'noExcept',
            label: `No ${filter.name} except`,
          },
        ];
      case 'number':
        return [
          {
            value: 'range',
            label: `Range`,
          },
          {
            value: 'moreThan',
            label: `More than`,
          },
          {
            value: 'lessThan',
            label: `Less than`,
          },
        ];
      default:
        return [];
    }
  }, [filter]);

  return (
    <Box
      data-testid={`filter-criteria-${filter.key}`}
      sx={{
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.grey[300],
      }}
      className="flex flex-col py-8"
    >
      <Box className="flex items-center justify-between">
        <Typography variant="h4">{filter.name}</Typography>

        {filter.active ? (
          <Button className="gap-8" onClick={() => handleClearFilter(filter.key)}>
            <ClearIcon fontSize="small" style={{ width: 14 }} />
            {t('clear')}
          </Button>
        ) : (
          <Button
            data-testid={`add-criteria-${filter.key}`}
            className="gap-8"
            onClick={() =>
              onFilterChange({
                key: filter.key,
                active: true,
              })
            }
          >
            <AddIcon fontSize="small" style={{ width: 14 }} />
            {t('addCriteria')}
          </Button>
        )}
      </Box>
      {filter.active && (
        <Box className="flex flex-col p-16">
          {filter.type !== 'date' && (
            <CriteriaRuleSelect
              criteriaRule={filter.rule}
              onCriteriaRuleChange={handleCriteriaRuleChange}
              options={criteriaRuleOptions}
            />
          )}

          {!!filter.rule &&
            filter.selectedValues &&
            (filter.type === 'string' ? (
              filter.searchType ? (
                <SearchableTextCriteriaContent
                  selectedFilterOptions={filter.selectedValues}
                  onSelectedFilterOptionsChange={handleSelectedOptionsChange}
                  filter={filter}
                />
              ) : (
                <TextCriteriaContent
                  selectedFilterOptions={filter.selectedValues}
                  onSelectedFilterOptionsChange={handleSelectedOptionsChange}
                  filter={filter}
                />
              )
            ) : filter.type === 'number' ? (
              <>
                {filter.rule === 'range' && (
                  <Box className="flex items-center gap-16">
                    <CurrencyInputBox
                      defaultValue={dollars(filter.minNumber)}
                      onChange={handleMinNumberChange}
                    />

                    <Typography fontWeight={400} color={'primary'}>
                      to
                    </Typography>

                    <CurrencyInputBox
                      defaultValue={dollars(filter.maxNumber)}
                      onChange={handleMaxNumberChange}
                    />
                  </Box>
                )}
                {filter.rule === 'moreThan' && (
                  <Box className="flex items-center">
                    <CurrencyInputBox
                      defaultValue={dollars(filter.minNumber)}
                      onChange={handleMinNumberChange}
                    />
                  </Box>
                )}
                {filter.rule === 'lessThan' && (
                  <Box className="flex items-center">
                    <CurrencyInputBox
                      defaultValue={dollars(filter.maxNumber)}
                      onChange={handleMaxNumberChange}
                    />
                  </Box>
                )}
              </>
            ) : (
              <></>
            ))}
        </Box>
      )}
    </Box>
  );
};
